import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { http } from "@/lib/api";
import { Sparkles, Send, X, Loader2 } from "lucide-react";

/**
 * FoodPulse Agent — floating chat widget mounted inside the CMS shell.
 * - Always ships the current route + document.title as `page_context` so
 *   answers stay grounded on what the admin is looking at.
 * - Falls back gracefully with a friendly "budget exhausted" state so it
 *   never crashes even when the Emergent LLM daily cap is hit.
 * - Renders catalog hits as clickable links so the admin can jump straight
 *   into the referenced product.
 */
export default function FoodPulseAgent() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [thread, setThread] = useState([]);
  const [busy, setBusy] = useState(false);
  const { pathname } = useLocation();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread, open]);

  const ask = async () => {
    const question = q.trim();
    if (!question || busy) return;
    setBusy(true);
    setThread((t) => [...t, { role: "user", text: question }]);
    setQ("");
    try {
      const r = await http.post("/cms/agent/ask", {
        question,
        include_catalog: true,
        page_context: {
          route: pathname,
          title: typeof document !== "undefined" ? document.title : "",
        },
      });
      setThread((t) => [...t, {
        role: "assistant",
        text: r.data.answer,
        catalog_hits: r.data.catalog_hits || [],
        budget_exhausted: !!r.data.budget_exhausted,
      }]);
    } catch (e) {
      setThread((t) => [...t, {
        role: "assistant",
        text: e.response?.data?.detail || "Something went wrong — please try again.",
        error: true,
      }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="fp-agent-open"
          className="fixed bottom-5 right-5 z-[9000] flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg transition hover:bg-emerald-700"
        >
          <Sparkles className="h-4 w-4" /> Ask FoodPulse Agent
        </button>
      )}
      {open && (
        <div
          data-testid="fp-agent-panel"
          className="fixed bottom-5 right-5 z-[9000] flex h-[520px] w-[380px] flex-col overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-emerald-100 bg-emerald-50 px-3 py-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-900">FoodPulse Agent</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="fp-agent-close"
              className="rounded-full p-1 text-emerald-700 hover:bg-emerald-100"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3">
            {thread.length === 0 && (
              <div className="mt-2 rounded-lg bg-emerald-50/60 p-3 text-xs text-emerald-900">
                Hi! Ask me anything about this page or the FoodPulse catalog — e.g. "Which recipes use ripe mango?" or "How do I mark a product recalled?"
              </div>
            )}
            {thread.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-emerald-600 text-white"
                    : m.error
                      ? "bg-rose-50 text-rose-800"
                      : "bg-slate-100 text-slate-900"
                }`}>
                  <p className="whitespace-pre-wrap">{m.text}</p>
                  {m.budget_exhausted && (
                    <p className="mt-1 text-[11px] text-amber-700">LLM budget exhausted — resumes on reset.</p>
                  )}
                  {m.catalog_hits?.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs">
                      {m.catalog_hits.slice(0, 3).map((h) => (
                        <li key={h.slug}>
                          <a href={`/food-pulse/items/${h.slug}`} className="text-emerald-700 hover:underline">→ {h.common_name}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 border-t border-slate-100 p-2">
            <input
              data-testid="fp-agent-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Ask about this page or the catalog…"
              className="flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-emerald-400"
              disabled={busy}
            />
            <button
              onClick={ask}
              disabled={busy || !q.trim()}
              data-testid="fp-agent-send"
              className="grid h-8 w-8 place-content-center rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-300"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
